import React, { useState, useEffect, useContext } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { Home, Shield, CheckCircle2, XCircle, Loader2 } from 'lucide-react';

export default function InviteAcceptPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useContext(AuthContext);
  const token = searchParams.get('token');

  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('Missing invite token in link.');
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const res = await axios.get(`${API}/employee-invites/verify/${token}`);
        setInvite(res.data);
      } catch (err) {
        setError(err.response?.data?.detail || 'This invite is invalid or has expired.');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const handleAccept = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (password !== confirm) {
      toast.error('Passwords do not match');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/employee-invites/accept`, { token, password });
      login(res.data.access_token, res.data.user);
      toast.success(`Welcome to the ${invite.landlord_name} team!`);
      navigate('/landlord/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to accept invite');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="glass-header bg-white/70 border-b border-border/40 py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 flex items-center gap-3">
          <Home className="w-7 h-7 text-primary" />
          <span className="text-2xl font-bold tracking-tight text-primary">PrimePocket</span>
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center p-6">
        <Card className="max-w-lg w-full p-8" data-testid="invite-accept-card">
          {loading ? (
            <div className="flex flex-col items-center gap-3 py-8">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
              <p className="text-muted-foreground">Verifying your invite...</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center gap-3 py-4 text-center" data-testid="invite-error">
              <XCircle className="w-12 h-12 text-destructive" />
              <h2 className="text-2xl font-semibold">Invite unavailable</h2>
              <p className="text-muted-foreground">{error}</p>
              <Button data-testid="invite-error-home-btn" variant="outline" onClick={() => navigate('/')} className="mt-2 rounded-full">
                Go to Home
              </Button>
            </div>
          ) : (
            <>
              <div className="flex flex-col items-center text-center mb-6">
                <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mb-3">
                  <Shield className="w-7 h-7 text-primary" />
                </div>
                <h2 className="text-3xl font-bold tracking-tight">You're invited!</h2>
                <p className="text-muted-foreground mt-2">
                  <span className="font-semibold text-foreground">{invite.landlord_name}</span> has invited you
                  to join their team on PrimePocket as a property manager.
                </p>
              </div>

              <div className="grid gap-2 mb-6 bg-muted/40 rounded-lg p-4 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Name</span><span className="font-medium">{invite.name}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Email</span><span className="font-medium">{invite.email}</span></div>
                <div>
                  <span className="text-muted-foreground text-sm">Permissions</span>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {(invite.permissions || []).length === 0 && (
                      <span className="text-xs text-muted-foreground italic">No permissions granted yet</span>
                    )}
                    {(invite.permissions || []).map((p) => (
                      <span key={p} className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary font-medium">
                        {p.replace(/_/g, ' ')}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              <form onSubmit={handleAccept} className="space-y-4">
                <div>
                  <Label>Set a Password</Label>
                  <Input
                    data-testid="invite-password-input"
                    type="password"
                    required
                    minLength={6}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="At least 6 characters"
                  />
                </div>
                <div>
                  <Label>Confirm Password</Label>
                  <Input
                    data-testid="invite-confirm-input"
                    type="password"
                    required
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                  />
                </div>
                <Button
                  data-testid="invite-accept-btn"
                  type="submit"
                  disabled={submitting}
                  className="w-full rounded-full bg-primary hover:bg-primary/90"
                >
                  {submitting ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Setting up your account...</>
                  ) : (
                    <><CheckCircle2 className="w-4 h-4 mr-2" /> Accept & Create Account</>
                  )}
                </Button>
              </form>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
